import type { BillingProductPrice } from "../types/billing";
import { getCurrencyLabel } from "../../../shared/helpers/format-monthly-price";

export function getPlanDisplayName(priceName: string) {
  return /^insights\b/i.test(priceName) ? priceName : `Insights ${priceName}`;
}

export function getPlanStorageMb(
  price: BillingProductPrice,
  seats: number,
) {
  if (price.pricingModel === "per_seat") {
    const baseSeats = Math.max(price.baseSeats, 1);
    const additionalSeats = Math.max(seats - baseSeats, 0);
    const additionalStorage =
      price.additionalSeatStorageMb ?? price.baseStorageMb;

    return price.baseStorageMb + additionalStorage * additionalSeats;
  }
  return price.baseStorageMb;
}

export function getPlanMonthlyCost(
  price: BillingProductPrice,
  seats: number,
) {
  if (price.pricingModel === "per_seat") {
    const baseSeats = Math.max(price.baseSeats, 1);
    const additionalSeats = Math.max(seats - baseSeats, 0);
    const additionalSeatPrice =
      price.additionalSeatPricePerMonth ?? price.basePricePerMonth;

    return price.basePricePerMonth + additionalSeatPrice * additionalSeats;
  }
  return price.basePricePerMonth;
}

export function formatPlanStorageAmount(storageMb: number) {
  if (storageMb >= 1_000) {
    return `${(storageMb / 1_000).toLocaleString()} GB`;
  }
  return `${storageMb.toLocaleString()} MB`;
}

export function formatPlanStorage(storageMb: number) {
  return `${formatPlanStorageAmount(storageMb)} storage`;
}

export function formatPlanUsers(
  pricingModel: BillingProductPrice["pricingModel"],
  seats: number,
) {
  if (pricingModel === "fixed") {
    return "Unlimited users";
  }
  return `${seats} active user${seats === 1 ? "" : "s"}`;
}

export function formatPerSeatPricingNote(
  price: BillingProductPrice,
  currency: string,
) {
  if (price.pricingModel !== "per_seat") {
    return null;
  }

  const minimumSeats = Math.max(price.baseSeats, 1);
  const unitCost =
    price.additionalSeatPricePerMonth ?? price.basePricePerMonth;
  const currencyLabel = getCurrencyLabel(currency);
  const baseSeatsLabel = `${minimumSeats} active seat${
    minimumSeats === 1 ? "" : "s"
  }`;

  return `${price.basePricePerMonth} ${currencyLabel} per month includes ${baseSeatsLabel}. Each additional active seat is ${unitCost} ${currencyLabel} per month.`;
}
